import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './UserProfile.css';

const UserProfile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'addresses'
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    fullName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false
  });

  useEffect(() => {
    // Load user data
    const user = JSON.parse(localStorage.getItem('userDetails') || '{}');
    setUserData(user);

    // Load profile data
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    setProfileData({
      name: profile.name || user.name || '',
      email: profile.email || user.email || '',
      phone: profile.phone || '',
    });

    // Load addresses
    const savedAddresses = JSON.parse(localStorage.getItem('userAddresses') || '[]');
    setAddresses(savedAddresses);
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  const handleSaveProfile = () => {
    // Save to localStorage
    localStorage.setItem('userProfile', JSON.stringify(profileData));

    // Also update userDetails to keep it in sync
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    userDetails.name = profileData.name;
    localStorage.setItem('userDetails', JSON.stringify(userDetails));

    // Trigger custom event to notify Header component
    window.dispatchEvent(new Event('userProfileUpdated'));

    setIsEditing(false);
    alert('✅ Profile updated successfully!');
  };


  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm({
      ...addressForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSaveAddress = () => {
    if (!addressForm.fullName || !addressForm.phone || !addressForm.address ||
      !addressForm.city || !addressForm.state || !addressForm.pincode) {
      alert('Please fill all address fields');
      return;
    }

    let updatedAddresses = [...addresses];

    if (editingAddressIndex !== null) {
      updatedAddresses[editingAddressIndex] = addressForm;
    } else {
      updatedAddresses.push(addressForm);
    }

    // If this address is set as default, remove default from others
    if (addressForm.isDefault) {
      updatedAddresses = updatedAddresses.map((addr, idx) => ({
        ...addr,
        isDefault: editingAddressIndex !== null ? idx === editingAddressIndex : idx === updatedAddresses.length - 1
      }));
    }

    localStorage.setItem('userAddresses', JSON.stringify(updatedAddresses));
    setAddresses(updatedAddresses);
    resetAddressForm();
    alert('✅ Address saved successfully!');
  };

  const handleEditAddress = (index) => {
    setAddressForm(addresses[index]);
    setEditingAddressIndex(index);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = (index) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      const updatedAddresses = addresses.filter((_, idx) => idx !== index);
      localStorage.setItem('userAddresses', JSON.stringify(updatedAddresses));
      setAddresses(updatedAddresses);
      alert('✅ Address deleted successfully!');
    }
  };

  const handleSetDefaultAddress = (index) => {
    const updatedAddresses = addresses.map((addr, idx) => ({
      ...addr,
      isDefault: idx === index
    }));
    localStorage.setItem('userAddresses', JSON.stringify(updatedAddresses));
    setAddresses(updatedAddresses);
  };

  const resetAddressForm = () => {
    setAddressForm({
      label: 'Home',
      fullName: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false
    });
    setShowAddressForm(false);
    setEditingAddressIndex(null);
  };

  return (
    <div className="user-profile-container">
      <div className="profile-wrapper">
        <div className="profile-sidebar">
          <div className="profile-user-info">
            <div className="profile-avatar">
              {profileData.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <h3>{profileData.name || 'User'}</h3>
            <p>{profileData.email}</p>
          </div>

          <nav className="profile-nav">
            <button
              className={`profile-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <span className="nav-icon">👤</span>
              Profile Details
            </button>
            <button
              className={`profile-nav-item ${activeTab === 'addresses' ? 'active' : ''}`}
              onClick={() => setActiveTab('addresses')}
            >
              <span className="nav-icon">📍</span>
              Saved Addresses
            </button>
            <button
              className="profile-nav-item"
              onClick={() => navigate('/order-history')}
            >
              <span className="nav-icon">📦</span>
              Order History
            </button>
          </nav>
        </div>

        <div className="profile-content">
          {activeTab === 'profile' && (
            <div className="profile-section">
              <div className="section-header">
                <h2>👤 Profile Information</h2>
                {!isEditing ? (
                  <button className="edit-btn" onClick={() => setIsEditing(true)}>
                    ✏️ Edit
                  </button>
                ) : (
                  <div className="edit-actions">
                    <button className="save-btn" onClick={handleSaveProfile}>
                      ✅ Save
                    </button>
                    <button className="cancel-btn" onClick={() => setIsEditing(false)}>
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="profile-form">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    className="form-input"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'addresses' && (
            <div className="addresses-section">
              <div className="section-header">
                <h2>📍 Saved Addresses</h2>
                <button
                  className="add-address-btn"
                  onClick={() => setShowAddressForm(!showAddressForm)}
                >
                  {showAddressForm ? '❌ Cancel' : '➕ Add New Address'}
                </button>
              </div>

              {showAddressForm && (
                <div className="address-form-card">
                  <h3>{editingAddressIndex !== null ? '✏️ Edit Address' : '➕ Add New Address'}</h3>
                  <div className="address-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Address Label</label>
                        <select
                          name="label"
                          value={addressForm.label}
                          onChange={handleAddressChange}
                          className="form-input"
                        >
                          <option value="Home">Home</option>
                          <option value="Work">Work</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="fullName"
                          value={addressForm.fullName}
                          onChange={handleAddressChange}
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={addressForm.phone}
                          onChange={handleAddressChange}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Pincode *</label>
                        <input
                          type="text"
                          name="pincode"
                          value={addressForm.pincode}
                          onChange={handleAddressChange}
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Full Address *</label>
                      <textarea
                        name="address"
                        value={addressForm.address}
                        onChange={handleAddressChange}
                        className="form-input"
                        rows="3"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>City *</label>
                        <input
                          type="text"
                          name="city"
                          value={addressForm.city}
                          onChange={handleAddressChange}
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>State *</label>
                        <input
                          type="text"
                          name="state"
                          value={addressForm.state}
                          onChange={handleAddressChange}
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="isDefault"
                          checked={addressForm.isDefault}
                          onChange={handleAddressChange}
                        />
                        Set as default address
                      </label>
                    </div>

                    <button className="save-address-btn" onClick={handleSaveAddress}>
                      💾 Save Address
                    </button>
                  </div>
                </div>
              )}

              <div className="addresses-list">
                {addresses.length === 0 ? (
                  <div className="empty-addresses">
                    <p>📭 No saved addresses yet</p>
                    <p>Add an address to make checkout faster!</p>
                  </div>
                ) : (
                  addresses.map((address, index) => (
                    <div key={index} className={`address-card ${address.isDefault ? 'default' : ''}`}>
                      <div className="address-header">
                        <span className="address-label">{address.label}</span>
                        {address.isDefault && <span className="default-badge">Default</span>}
                      </div>
                      <div className="address-body">
                        <h4>{address.fullName}</h4>
                        <p>{address.address}</p>
                        <p>{address.city}, {address.state} - {address.pincode}</p>
                        <p>📞 {address.phone}</p>
                      </div>
                      <div className="address-actions">
                        {!address.isDefault && (
                          <button
                            className="set-default-btn"
                            onClick={() => handleSetDefaultAddress(index)}
                          >
                            Set as Default
                          </button>
                        )}
                        <button
                          className="edit-address-btn"
                          onClick={() => handleEditAddress(index)}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="delete-address-btn"
                          onClick={() => handleDeleteAddress(index)}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
